import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import SmsSignup from "@/pages/SmsSignup";
import PublicPacket from "@/pages/PublicPacket";
import Packets from "@/pages/Packets";
import PacketDetail from "@/pages/PacketDetail";
import PublicProposal from "@/pages/PublicProposal";
import Proposals from "@/pages/Proposals";
import ProposalDetail from "@/pages/ProposalDetail";
import Dashboard from "@/pages/Dashboard";
import ManagerDashboard from "@/pages/ManagerDashboard";
import Workers from "@/pages/Workers";
import Clients from "@/pages/Clients";
import Projects from "@/pages/Projects";
import Assignments from "@/pages/Assignments";
import Timesheets from "@/pages/Timesheets";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import PayRuns from "@/pages/PayRuns";
import YearEnd from "@/pages/YearEnd";
import Reports from "@/pages/Reports";
import Audit from "@/pages/Audit";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import SentEmails from "@/pages/SentEmails";
import LaunchReadiness from "@/pages/LaunchReadiness";
import Announcements from "@/pages/Announcements";
import Recruitment from "@/pages/Recruitment";
import RecruitRespond from "@/pages/RecruitRespond";
import Help from "@/pages/Help";
import ApproveSuggestion from "@/pages/ApproveSuggestion";
import ContractorHome from "@/pages/ContractorHome";
import ContractorTimesheet from "@/pages/ContractorTimesheet";
import SupervisorApproval from "@/pages/SupervisorApproval";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import NoWorkPublic from "@/pages/NoWorkPublic";
import TaxInfoPublic from "@/pages/TaxInfoPublic";
import AdminLayout from "@/components/AdminLayout";
import WorkerDetail from "@/pages/WorkerDetail";
import SalesReps from "@/pages/SalesReps";
import SalesLayout from "@/components/SalesLayout";
import SalesHome from "@/pages/sales/SalesHome";
import SalesComp from "@/pages/sales/SalesComp";
import SalesProposals from "@/pages/sales/SalesProposals";
import SalesProposalDetail from "@/pages/sales/SalesProposalDetail";
import SalesPackets from "@/pages/sales/SalesPackets";
import SalesPacketDetail from "@/pages/sales/SalesPacketDetail";
import SalesWorkers from "@/pages/sales/SalesWorkers";
import ForcePasswordChange from "@/components/ForcePasswordChange";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { Toaster } from "sonner";
import UpdateBanner from "@/components/UpdateBanner";
import { homeFor } from "@/lib/home";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function RootDashboard() {
  const { user } = useAuth();
  if (user?.role === "manager") return <ManagerDashboard />;
  return <Dashboard />;
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster richColors position="top-right" />
      <UpdateBanner />
      <PWAInstallPrompt />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/sms-signup" element={<SmsSignup />} />
          <Route path="/contractor-packet/:token" element={<PublicPacket />} />
          <Route path="/proposal/:token" element={<PublicProposal />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/supervisor/approve/:token" element={<SupervisorApproval />} />
          <Route path="/recruit/:token" element={<RecruitRespond />} />
          <Route path="/approve-suggestion/:token" element={<ApproveSuggestion />} />
          <Route path="/no-work/:token" element={<NoWorkPublic />} />
          <Route path="/tax-info/:token" element={<TaxInfoPublic />} />

          <Route path="/contractor" element={<Protected roles={["contractor"]}><ContractorHome /></Protected>} />
          <Route path="/contractor/new" element={<Protected roles={["contractor"]}><ContractorTimesheet /></Protected>} />
          <Route path="/contractor/edit/:id" element={<Protected roles={["contractor"]}><ContractorTimesheet /></Protected>} />

          <Route path="/sales" element={<Protected roles={["sales"]}><SalesLayout /></Protected>}>
            <Route index element={<SalesHome />} />
            <Route path="comp" element={<SalesComp />} />
            <Route path="proposals" element={<SalesProposals />} />
            <Route path="proposals/:id" element={<SalesProposalDetail />} />
            <Route path="packets" element={<SalesPackets />} />
            <Route path="packets/:id" element={<SalesPacketDetail />} />
            <Route path="workers" element={<SalesWorkers />} />
          </Route>

          <Route element={<Protected roles={["admin", "analyst", "manager"]}><AdminLayout /></Protected>}>
            <Route path="/" element={<RootDashboard />} />
            <Route path="/workers" element={<Workers />} />
            <Route path="/workers/:id" element={<Protected roles={["admin", "analyst"]}><WorkerDetail /></Protected>} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/assignments" element={<Assignments />} />
            <Route path="/timesheets" element={<Timesheets />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/invoices/:id" element={<InvoiceDetail />} />
            <Route path="/payruns" element={<PayRuns />} />
            <Route path="/year-end" element={<YearEnd />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/audit" element={<Audit />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/users" element={<Protected roles={["admin"]}><Users /></Protected>} />
            <Route path="/sent-emails" element={<SentEmails />} />
            <Route path="/packets" element={<Packets />} />
            <Route path="/packets/:id" element={<PacketDetail />} />
            <Route path="/proposals" element={<Proposals />} />
            <Route path="/proposals/:id" element={<ProposalDetail />} />
            <Route path="/sales-reps" element={<Protected roles={["admin", "analyst"]}><SalesReps /></Protected>} />
            <Route path="/launch-readiness" element={<Protected roles={["admin"]}><LaunchReadiness /></Protected>} />
            <Route path="/announcements" element={<Protected roles={["admin"]}><Announcements /></Protected>} />
            <Route path="/recruitment" element={<Protected roles={["admin"]}><Recruitment /></Protected>} />
            <Route path="/help" element={<Protected roles={["admin", "analyst"]}><Help /></Protected>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
